import { useForm } from '@tanstack/react-form';
import { useAtom, useSetAtom } from 'jotai';
import { uniqueId } from 'lodash-es';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useHistory } from 'react-router-dom';
import routes from '~/routes';
import type { AreaSelection, TaxScenarioFormData } from './types';
import { saveScenarioLoadableAtom, toastAtom } from './atoms';
import {
  buildCreatePayload,
  buildUpdatePayload,
  createDefaultTaxEntries,
  DEFAULT_AREA_SELECTION,
  GLOBAL_SELECTION_ID,
  isAreaSelectionComplete,
  type FormMode,
} from './utils';
import { getMemoizedOverlappingAreaIds } from 'utils/geometry-overlap';

interface ScenarioFormContextType {
  form: ReturnType<typeof useCreateForm>;
  mode: FormMode;
  selectedId: string | number;
  setSelectedId: (id: string | number) => void;
  addArea: () => void;
  removeArea: (id: number | string) => void;
  handleSave: () => void;
  isSaving: boolean;
}

const ScenarioFormContext = createContext<ScenarioFormContextType | null>(null);

interface ScenarioFormProviderProps {
  initialValues: TaxScenarioFormData;
  mode: FormMode;
  scenarioId?: string;
  geometries: Record<string, GeoJSON.Geometry>;
  children: ReactNode;
}

const useCreateForm = (
  defaultValues: TaxScenarioFormData,
  geometries: Record<string, GeoJSON.Geometry>
) => {
  return useForm({
    defaultValues,
    validators: {
      onChange: ({ value }: { value: TaxScenarioFormData }) => {
        const hasIncompleteArea = value.areas.some(
          (a) => !isAreaSelectionComplete(a)
        );
        if (hasIncompleteArea) return 'All areas must have a selection';

        const ids = getMemoizedOverlappingAreaIds(value.areas, geometries);
        if (ids.size > 0)
          return 'One or more areas have overlapping geometries';

        return undefined;
      },
    },
  });
};

/**
 * Provides form state management for the tax scenario form.
 * @param {ScenarioFormProviderProps} props - The provider props
 * @return {JSX.Element} The provider component
 */
export function ScenarioFormProvider({
  initialValues,
  mode,
  scenarioId,
  geometries,
  children,
}: ScenarioFormProviderProps): JSX.Element {
  const form = useCreateForm(initialValues, geometries);
  const [selectedId, setSelectedId] = useState<string | number>(
    GLOBAL_SELECTION_ID
  );
  const [saveState, triggerSave] = useAtom(saveScenarioLoadableAtom);
  const removedAreaIdsRef = useRef<number[]>([]);
  const history = useHistory();

  const isSaving = saveState.state === 'loading';
  const setToast = useSetAtom(toastAtom);

  const addArea = useCallback(() => {
    const id = Number(uniqueId());
    const currentAreas = form.getFieldValue('areas');
    form.setFieldValue('areas', [
      ...currentAreas,
      { ...DEFAULT_AREA_SELECTION, id, taxEntries: createDefaultTaxEntries() },
    ]);
    setSelectedId(id);
  }, [form]);

  const removeArea = useCallback(
    (id: number | string) => {
      const currentAreas = form.getFieldValue('areas');
      const area = currentAreas.find((a: AreaSelection) => a.id === id);
      if (area?.status === 'saved') {
        removedAreaIdsRef.current.push(area.id);
      }
      form.setFieldValue(
        'areas',
        currentAreas.filter((a: AreaSelection) => a.id !== id)
      );

      if (selectedId === id) {
        setSelectedId(GLOBAL_SELECTION_ID);
      }
    },
    [form, selectedId]
  );

  const handleSave = useCallback(async () => {
    const values = form.state.values;
    setToast(null);

    if (mode === 'create') {
      const payload = buildCreatePayload(values);
      try {
        const response = await triggerSave({ mode: 'create', payload });
        setToast({ message: 'Scenario saved', type: 'success' });
        history.replace(
          routes.taxScenarioDetailPath.replace(':id', String(response.id))
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save scenario';
        setToast({ message, type: 'error' });
      }
    } else {
      const payload = buildUpdatePayload(values, removedAreaIdsRef.current);
      try {
        await triggerSave({
          mode: 'edit',
          id: Number(scenarioId),
          payload,
        });
        setToast({ message: 'Scenario saved', type: 'success' });
        window.location.reload();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save scenario';
        setToast({ message, type: 'error' });
      }
    }
  }, [form, mode, scenarioId, triggerSave, history, setToast]);

  const value = useMemo(
    () => ({
      form,
      mode,
      selectedId,
      setSelectedId,
      addArea,
      removeArea,
      handleSave,
      isSaving,
    }),
    [form, mode, selectedId, addArea, removeArea, handleSave, isSaving]
  );

  return (
    <ScenarioFormContext.Provider value={value}>
      {children}
    </ScenarioFormContext.Provider>
  );
}

/**
 * Returns the scenario form context.
 * @return {ScenarioFormContextType} The scenario form context
 */
export function useScenarioForm(): ScenarioFormContextType {
  const context = useContext(ScenarioFormContext);
  if (context === null) {
    throw new Error(
      'useScenarioForm must be used within a ScenarioFormProvider'
    );
  }
  return context;
}
